import type { FormItemProps } from 'tea-component';
import type { FieldRenderProps } from 'react-final-form-hooks/dist';

export const getNullableURL = (url?: string): URL | null => {
  if (!url) return null;

  try {
    return new URL(url);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getSimpleFileName = (fileName?: string): string => {
  if (!fileName) return '';
  const dotIndex = fileName.lastIndexOf('.');
  return fileName.substring(0, dotIndex);
};

export const formatTeaFormItemStatus = (
  showError: boolean,
  fieldMeta: FieldRenderProps['meta'],
): FormItemProps['status'] => {
  if (!showError) return 'success';
  const { touched, validating, valid } = fieldMeta || {};
  if (!touched) return 'success';
  if (validating) return 'validating';
  return valid ? 'success' : 'error';
};

/**
 * 检查给定的 URL 是否是一个 Data URL。
 *
 * @param url - 要检查的 URL 字符串。
 * @returns 如果 URL 是 Data URL，则返回 `true`；否则返回 `false`。
 */
export const isDataURL = (url: string): boolean => {
  // Data URL 的正则表达式
  const dataURLRegex = /^data:[^;]*;base64,(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

  return dataURLRegex.test(url);
};

/**
 * 将 Base64 图片数据转换为 File 对象
 * @param base64Data Base64 格式的图片数据，例如 "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA..."
 * @param fileName 可选，生成的 File 对象的文件名，默认为 'image.jpg'
 * @returns Promise<File> 返回一个 Promise，解析为图片的 File 对象
 */
export const getFileFromBase64 = async (base64Data: string, fileName = 'image.jpg'): Promise<File> => {
  try {
    // 1. 从 base64 字符串中提取 MIME 类型和纯 base64 数据部分
    const matches = base64Data.match(/^data:(.+?);base64,(.+)$/);
    if (!matches || matches.length !== 3) {
      throw new Error('Invalid base64 image format');
    }

    const mimeType = matches[1]; // 例如 "image/png"
    const base64String = matches[2]; // 纯 base64 数据部分

    // 2. 将 base64 字符串转换为 ArrayBuffer
    const byteCharacters = atob(base64String); // 解码 base64
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    // 3. 创建 Blob 对象
    const blob = new Blob(byteArrays, { type: mimeType });

    // 4. 将 Blob 转换为 File 对象
    const file = new File([blob], fileName, { type: mimeType });

    return file;
  } catch (error) {
    console.error('Error converting base64 to File:', error);
    throw error; // 抛出错误供调用方处理
  }
};

/**
 * 从指定的图片 URL 加载图片并返回一个 File 对象。
 *
 * @param url - 图片的 URL 地址。
 * @param fileName - 生成的 File 对象的文件名。
 * @returns 返回一个 Promise，解析为包含加载的图片数据的 File 对象。
 * @throws 如果图片加载失败或请求出错，将抛出错误。
 */
export const loadImageAsFile = async (url: string, fileName: string): Promise<File> => {
  try {
    // 获取图片数据
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    // 获取图片的 MIME 类型
    const mimeType = response.headers.get('content-type') || 'image/jpeg';
    const blob = await response.blob();

    // 创建 File 对象
    return new File([blob], fileName, { type: mimeType });
  } catch (error) {
    console.error('Error loading image:', error);
    throw error;
  }
};
